import { booleanAttribute, computed, Directive, effect, ElementRef, inject, input } from '@angular/core';

import { MibListItemSize } from './list-item.models';

const ROW =
  'group flex w-full min-w-0 items-center gap-3 rounded-mib px-3 text-left font-medium select-none cursor-pointer transition-colors ' +
  'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mib-primary ' +
  'disabled:cursor-not-allowed disabled:opacity-40 ' +
  'aria-disabled:cursor-not-allowed aria-disabled:opacity-40 aria-disabled:pointer-events-none ' +
  '[&>svg[mib-icon]]:shrink-0';

const IDLE = 'text-gray-900 dark:text-white not-disabled:hover:bg-gray-100 dark:not-disabled:hover:bg-white/5';
const ACTIVE = 'text-mib-primary bg-mib-indigo-50 dark:text-white dark:bg-gray-700';

// An icon carrying its own size-* keeps it: the row only sizes the icons the page left unsized.
const SIZE: Record<MibListItemSize, string> = {
  sm: 'min-h-8 text-sm [&>svg[mib-icon]:not([class*=size-])]:size-4',
  md: 'min-h-9 text-sm [&>svg[mib-icon]:not([class*=size-])]:size-5',
  lg: 'min-h-10 text-base [&>svg[mib-icon]:not([class*=size-])]:size-6',
  xl: 'min-h-11 text-base [&>svg[mib-icon]:not([class*=size-])]:size-7',
};

/**
 * A clickable row of a list, applied to a native `<button>` or `<a>`: it adds classes and nothing else.
 *
 * ```html
 * <a mibListItem href="/lives" [active]="true"><svg mib-icon name="video"></svg><span class="truncate">Lives</span></a>
 * ```
 */
@Directive({
  selector: 'button[mibListItem], a[mibListItem]',
  host: {
    '[class]': 'classes()',
  },
})
export class MibListItemDirective {
  /** The height, on the control scale: `sm` 32px, `md` 36px, `lg` 40px, `xl` 44px. */
  readonly size = input<MibListItemSize>('md');
  /** Draws the row as the selected one; on an `<a>` it also writes `aria-current="page"`. */
  readonly active = input(false, { transform: booleanAttribute });

  protected readonly classes = computed(() => `${ROW} ${SIZE[this.size()]} ${this.active() ? ACTIVE : IDLE}`);

  constructor() {
    const host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    // Written only on a link: a host binding would write null on a button and wipe the page's own aria-current.
    if (host.tagName !== 'A') return;
    effect(() => (this.active() ? host.setAttribute('aria-current', 'page') : host.removeAttribute('aria-current')));
  }
}
